import {
  CANCELED,
  UPDATED,
  CORRECTED,
  REPEATED,
  USABLE
} from "@/constants/newsItemStatus";
import { getFilterRuleTypeBySubject } from "@/constants/subjects";
import locale from "@/locales/messages/de";
import { NewsItem } from "./types.d";

const getNewItemByGUID = ({ newsItems }) => (guid): NewsItem =>
  newsItems.find(newsItem => newsItem.guid === guid);

interface ClassificationFilterRule {
  text?: string;
  type: string;
  values: string[];
}

/**
 * getSubjectFilterRulesByType - gets the list of filterRules based on the "isGeoArea"
 * flag from a list of subject objects
 * @param {Boolean} isGeoArea - whether geoArea filterRules are mapped
 * @param {Array} subject - the list of subject objects
 * @returns {Function} mapper - a function which accepts the subject array
 */
const getSubjectFilterRulesByType = (isGeoArea: boolean) => subject =>
  subject
    .filter(({ type }) => (isGeoArea ? type === "geoArea" : type !== "geoArea"))
    .map(({ type, name, uri }) => ({
      text: name,
      type: getFilterRuleTypeBySubject(type),
      // If type is 'FILTER_FULLTEXT' the name should be used
      values: [
        getFilterRuleTypeBySubject(type) === "FILTER_FULLTEXT"
          ? name
          : uri || name
      ]
    }));

/** getGeoAreaFilterRules - filters and maps subject to geoAea filterRules
 * @param {Array} subject - the list of subject objects
 * @returns {Array} filterRules - a list of geoArea filterRules
 * */
export const getGeoAreaFilterRules = getSubjectFilterRulesByType(true);

/** getRessortsFilterRules - filters and maps subject to Ressort filterRules
 * @param {Array} subject - the list of subject objects
 * @returns {Array} filterRules - a list of Ressort filterRules
 * */
export const getRessortsFilterRules = getSubjectFilterRulesByType(false);

/** getPriorityFilterRules - maps urgency to Priority filterRules
 * @param {Number} urgency - the urgency of a newsItem
 * @returns {Array} filterRules - a list(length = 1) of priority filterRules
 * */
export const getPriorityFilterRules = (urgency: number) => {
  if (urgency <= 2) {
    return [{ text: "Eilmeldung", type: "FILTER_URGENCY", values: ["1", "2"] }];
  }

  return [];
};

export const getKeywordFillterRules = keyword =>
  keyword.map(text => ({ text, type: "FILTER_FULLTEXT", values: [text] }));

export const getClassificationFilterRules = ({
  urgency,
  subject,
  keyword
}): ClassificationFilterRule[] => [
  ...getPriorityFilterRules(urgency),
  ...getRessortsFilterRules(subject),
  ...getKeywordFillterRules(keyword),
  ...getGeoAreaFilterRules(subject)
];

export const getNewsItemTags = ({ urgency, subject, keyword }): string[] =>
  getClassificationFilterRules({ urgency, subject, keyword }).map(
    ({ text }) => text
  );

export const getNewsItemStatus = (
  { pubStatus, signal, ednote },
  asSignal: boolean = false // NB: to map ednote to tooltip text
): string => {
  // WIP, TODO: Order of signals?
  let usableSignal = "";
  const hasUpdated = signal.some(s => s === UPDATED);
  const hasCorrected =
    signal.some(s => s === CORRECTED) ||
    ednote.some(({ role }) => role === CORRECTED);
  const hasRepeated = signal.some(s => s === REPEATED);
  if (hasRepeated && !hasUpdated && !hasCorrected) {
    usableSignal = REPEATED;
  } else if (hasUpdated && hasCorrected) {
    // WIP, TODO: Take into consideration the latest related ednote
    // when deciding over updated or corrected.
    usableSignal = signal.reduceRight(
      (acc, s) => (!acc && (s === UPDATED || CORRECTED) ? s : acc),
      ""
    );
  } else if (hasUpdated || hasCorrected) {
    usableSignal = hasUpdated ? UPDATED : CORRECTED;
  }

  switch (pubStatus) {
    case CANCELED:
      return asSignal ? "" : locale[`status:${CANCELED}`];
    case USABLE:
      return asSignal ? usableSignal : locale[`status:${usableSignal}`];
    default:
      return "";
  }
};

// NB: pubStatus may not necessarily match any valid ednote, e.g.
// status "repeat" contains "correction" note
// const asSignal = true;
// const pubStatus = getNewsItemStatus(newsItem, asSignal);
export const getNewsItemStatusTooltip = (newsItem): string =>
  newsItem.ednote.reduceRight((acc, n) => {
    if (!acc && n.role) {
      return n.text;
    }

    // EC-584: prefer 'correction' over 'correctionshort'
    if (acc && n.role === "correction") {
      return n.text;
    }

    return acc;
  }, "");

const getStatus = ({ newsItems }) => ({ guid }): string => {
  const newsItem: NewsItem = getNewItemByGUID({ newsItems })(guid);

  return getNewsItemStatus(newsItem);
};

const getClassificationFilterRulesByGUID = ({ newsItems }) => ({
  guid
}): ClassificationFilterRule[] => {
  const newsItem: NewsItem = getNewItemByGUID({ newsItems })(guid);

  return getClassificationFilterRules(newsItem);
};

const getTags = ({ newsItems }) => ({ guid }): string[] => {
  const newsItem: NewsItem = getNewItemByGUID({ newsItems })(guid);

  return getNewsItemTags(newsItem);
};

const getEncodedImageById = ({ encodedImages }) => guid => encodedImages[guid];

export default {
  getNewItemByGUID,
  getStatus,
  getClassificationFilterRulesByGUID,
  getTags,
  getEncodedImageById
};
